import { Link } from "react-router-dom";
import { useRef } from "react";
import { Quote, MapPin, ChevronLeft, ChevronRight, BadgeCheck } from "lucide-react";
import { COUNTRIES } from "@/pages/ReviewInvite";
import { useLang } from "@/context/LangContext";
import { useApi, API } from "@/lib/api";
import { Stars } from "@/components/site/DestinationCard";


export const ReviewCard = ({ r, showPlace }) => {
  const { t, lang } = useLang();
  const date = new Date(r.created_at).toLocaleDateString({ id: "id-ID", en: "en-GB", ms: "ms-MY", fr: "fr-FR", de: "de-DE" }[lang], { day: "numeric", month: "short", year: "numeric" });
  return (
    <div data-testid={`review-card-${r.id}`} className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-lg">
      <div className="flex items-center justify-between"><Stars value={r.rating} /><Quote className="h-6 w-6 text-amber-300" /></div>
      <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-700">“{r.comment}”</p>
      {r.photos?.length > 0 && (
        <div data-testid={`review-photos-${r.id}`} className="mt-3 flex gap-2">
          {r.photos.map((p) => (
            <a key={p} href={`${API}/review-photos/${p}`} target="_blank" rel="noreferrer" className="block h-16 w-16 overflow-hidden rounded-xl ring-1 ring-slate-200">
              <img src={`${API}/review-photos/${p}`} alt="" loading="lazy" className="h-full w-full object-cover transition-transform hover:scale-110" />
            </a>
          ))}
        </div>
      )}
      <div className="mt-5 flex items-center gap-3 border-t border-slate-100 pt-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#064E3B] font-bold text-white">{r.user_name[0]}</span>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-slate-900">{r.user_name}{r.country && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-extrabold text-amber-800">{COUNTRIES[r.country] || r.country}</span>}</p>
          <p className="flex items-center gap-1 text-xs text-slate-500">{date}{r.verified && <span data-testid={`review-verified-${r.id}`} className="ml-1 inline-flex items-center gap-0.5 font-bold text-emerald-700"><BadgeCheck className="h-3.5 w-3.5" />{t("verified_booking")}</span>}</p>
        </div>
      </div>
      {showPlace && r.destination_name && (
        <Link to={`/destinations/${r.destination_slug}`} data-testid={`review-place-${r.id}`} className="mt-3 flex items-center gap-1 text-xs font-bold text-[#E11D48] hover:underline"><MapPin className="h-3.5 w-3.5" />{r.destination_name}</Link>
      )}
    </div>
  );
};

export default function Testimonials() {
  const { t, sel } = useLang();
  const { data } = useApi(`/reviews/top?limit=10&lang=${sel}`);
  const track = useRef(null);
  if (!data?.length) return null;
  return (
    <section data-testid="testimonials-section" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{t("testi_title")}</h2>
          <p className="mt-2 text-base text-slate-500">{t("testi_sub")}</p>
        </div>
        <div className="hidden sm:flex gap-2">
          {[[-1, ChevronLeft, "prev"], [1, ChevronRight, "next"]].map(([d, Icon, k]) => (
            <button key={k} data-testid={`testimonials-${k}-btn`} onClick={() => track.current?.scrollBy({ left: d * 360, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" })} className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 transition-colors hover:bg-slate-100"><Icon className="h-5 w-5" /></button>
          ))}
        </div>
      </div>
      <div ref={track} data-testid="testimonials-track" className="no-scrollbar mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2">
        {data.map((r) => <div key={r.id} className="w-[290px] sm:w-[340px] shrink-0 snap-start"><ReviewCard r={r} showPlace /></div>)}
      </div>
    </section>
  );
}
